// client/src/components/common/phoneLayout.ts
//
// The phone layout of the LexiStar controller pages (ST-00207, m4b-contracts §5): Deviation
// Approvals, Trip detail, the Status Board and the management dashboard switch to card lists,
// filter sheets, a sticky bottom action bar and a map panel with a full-screen toggle at the
// same point the shared date pickers open as a bottom sheet — a viewport under 640 px or a coarse
// (touch) primary pointer (PICKER_SHEET_QUERY, pickerHooks.ts). No matchMedia (jsdom, old
// browsers) reads as desktop, so the desktop pages — and every test written for them — are
// unchanged.

import { PICKER_SHEET_QUERY, usePickerSheetMode } from './pickerHooks';

/** `(max-width: 639.98px), (pointer: coarse)` — also the `.touch-target` breakpoint (index.css). */
export const PHONE_LAYOUT_QUERY = PICKER_SHEET_QUERY;

/** True while a page should render its phone layout; follows rotation and window resizes. */
export function usePhoneLayout(): boolean {
  return usePickerSheetMode();
}

/**
 * Scroll the content pane `node` sits in (the shell's `.page-pane`, or a tab pane) back to the
 * top — a phone page that swaps a list for a detail view starts the detail at its heading.
 */
export function scrollPaneToTop(node: Element | null | undefined): void {
  const pane = node?.closest('.page-pane') ?? (typeof document !== 'undefined' ? document.scrollingElement : null);
  if (pane) pane.scrollTop = 0;
}

/** Bring `node` into view (nearest edge; smooth unless the user asked for reduced motion). */
export function revealNode(node: HTMLElement | null | undefined): void {
  if (!node || typeof node.scrollIntoView !== 'function') return;
  const reduce =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    !!window.matchMedia('(prefers-reduced-motion: reduce)')?.matches;
  node.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
}
